import React, { useState, useRef, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { Productbtn } from "../../../../../../shared/services/interfacev2";
import { useUserTheme } from "src/shared/utils/useUserTheme";
import { borderHighlightOptions } from "src/shared/modules/profile/theme/themeConstants";
import { useUser } from "src/shared/context/UserContext";

interface ProductItemProps {
  product: Productbtn;
  quantity: number;
  displayAmount: string;
  onQuantityChange: (productId: string, amount: string, type: "add" | "subtract") => void;
  onProductRemove: (id: string, amount: string) => void;
  onAmountEdit?: (productId: string, newAmount: string) => void;
  isAmountEditable?: boolean;
}

const ProductItem: React.FC<ProductItemProps> = ({ product, quantity, displayAmount, onQuantityChange, onProductRemove, onAmountEdit, isAmountEditable = true }) => {
  const { user } = useUser();
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState(displayAmount);
  const [isValidAmount, setIsValidAmount] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const themeKey = user?.themes.backgroundColour || "DodgerBlue";
  const highlightClass = borderHighlightOptions[themeKey];
  const { bgClass, fontClass } = useUserTheme();

  useEffect(() => {
    if (isEditingAmount && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingAmount]);

  const handleAmountClick = () => {
    if (isAmountEditable && onAmountEdit && !isEditingAmount) {
      setIsEditingAmount(true);
      setEditedAmount(displayAmount);
      setIsValidAmount(true);
    }
  };

  const validateAmount = (value: string): boolean => {
    const numValue = parseFloat(value);
    return !isNaN(numValue) && numValue > 0 && numValue <= 999999.99;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEditedAmount(value);
    setIsValidAmount(validateAmount(value));
  };

  const handleAmountSave = () => {
    if (isValidAmount && onAmountEdit) {
      const numericAmount = parseFloat(editedAmount);
      if (!isNaN(numericAmount) && numericAmount > 0) {
        const formattedAmount = numericAmount.toFixed(2);
        onAmountEdit(product._id.$oid, formattedAmount);
        setIsEditingAmount(false);
      }
    } else {
      setIsEditingAmount(false);
      setEditedAmount(displayAmount);
      setIsValidAmount(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAmountSave();
    } else if (e.key === "Escape") {
      setIsEditingAmount(false);
      setEditedAmount(displayAmount);
      setIsValidAmount(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (isEditingAmount) {
        handleAmountSave();
      }
    }, 150);
  };

  const totalPrice = (quantity * parseFloat(displayAmount)).toFixed(2);

  return (
    <div
      className={`relative flex flex-col sm:flex-row sm:items-center justify-between rounded-xl p-2 sm:p-2 mb-2 shadow-md hover:shadow-lg transition-all duration-200 bg-gray-50 border-2 
       ${fontClass} ${highlightClass ? `${highlightClass} ring-1 ring-opacity-40` : "border-transparent"}`}
    >
      <button
        onClick={() => onProductRemove(product._id.$oid, product.amount)}
        className="absolute -top-1.5 -right-1.5 sm:-top-1 sm:-right-1 flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600 border border-red-200 hover:bg-red-600 hover:text-white hover:scale-110 transition-all duration-200 shadow-sm"
      >
        <CloseIcon fontSize="small" />
      </button>

      <div className="flex flex-col flex-1 min-w-0 pr-6 sm:pr-0">
        <p className={`text-base font-semibold truncate text-gray-800`}>{product.productName}</p>
        {product.arabicName && <p className="text-sm text-gray-700 truncate leading-tight mt-1">{product.arabicName}</p>}
      </div>

      <div className="flex items-center justify-between sm:justify-end gap-4 mt-3 sm:mt-0 sm:ml-4 sm:min-w-[240px] w-full sm:w-auto">
        <div className="flex items-center gap-2">
          <button className={`rounded-full p-1 hover:opacity-90 transition-all ${bgClass}`} onClick={() => onQuantityChange(product._id.$oid, product.amount, "subtract")}>
            <RemoveIcon fontSize="small" />
          </button>
          <span className="px-3 py-1 rounded text-center min-w-[38px] border border-gray-300 text-sm text-bold sm:text-base font-bold bg-white shadow-md">{quantity}</span>
          <button className={`rounded-full p-1 hover:opacity-90 transition-all ${bgClass}`} onClick={() => onQuantityChange(product._id.$oid, product.amount, "add")}>
            <AddIcon fontSize="small" />
          </button>
        </div>

        <div className="flex items-center gap-1">
          {isEditingAmount ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="number"
                step="0.01"
                min="0.01"
                value={editedAmount}
                onChange={handleAmountChange}
                onKeyDown={handleKeyPress}
                onBlur={handleBlur}
                className={`w-20 px-2 py-1 border-2 rounded text-right text-sm font-semibold ${
                  isValidAmount ? "border-blue-500 bg-blue-50" : "border-red-500 bg-red-50"
                } focus:outline-none focus:ring-2 focus:ring-blue-300`}
                placeholder="0.00"
              />
              <span className="text-gray-600 text-sm font-medium">QAR</span>
              <button
                onClick={handleAmountSave}
                disabled={!isValidAmount}
                className={`p-1 rounded-full ${isValidAmount ? "bg-green-500 hover:bg-green-600" : "bg-gray-300 cursor-not-allowed"} text-white transition-colors`}
              >
                <CheckIcon fontSize="small" />
              </button>
            </div>
          ) : (
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${isAmountEditable ? "cursor-pointer hover:bg-blue-50" : ""}`}
              onClick={handleAmountClick}
              title={isAmountEditable ? "Click to edit price" : ""}
            >
              <p className={`text-[#D70015] font-semibold text-sm sm:text-base text-right min-w-[90px] ${isAmountEditable ? "hover:text-blue-600" : ""}`}>{totalPrice} QAR</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductItem;
